import { z } from 'zod/v4';
import { trpc } from '@org/clients/school';
import { DynamicForm } from '@org/ui';
import { toast } from 'sonner';
import { useState, forwardRef, useImperativeHandle, useMemo, useRef } from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { WomanSelect } from '../woman/woman-select';

// Define the unit schema directly here since we don't have it in the model export.
// The woman of the unit is picked from the Women page (`womanId`); her name,
// portrait, quote and seal live on her own record now.
const buildUnitSchema = (t: TFunction<'admin'>) =>
  z.object({
    unitId: z.string().optional(),
    name: z.string().min(1, t('units.form.errors.nameRequired')).describe('units.form.labels.name'),
    description: z
      .string()
      .max(2000, t('units.form.errors.descriptionTooLong'))
      .describe('units.form.labels.description')
      .nullable()
      .optional(),
    womanId: z.string().nullable().optional(),
    empowermentPoints: z.coerce.number().int().min(0).optional(),
  });

type UnitFormValues = z.infer<ReturnType<typeof buildUnitSchema>>;

type WomanFieldContext = {
  field: { value: unknown; onChange: (value: unknown) => void };
};

export interface UnitFormProps {
  defaultValues: any; 
  onSubmit: (data: any) => void; 
  onClose: () => void;
  hideButtons?: boolean;
  /** Fires when the fields differ from the last saved values (and when they are back in sync). */
  onDirtyChange?: (dirty: boolean) => void;
}

export interface UnitFormRef {
  submit: () => void;
}

export const UnitForm = forwardRef<UnitFormRef, UnitFormProps>(({
  defaultValues, 
  onSubmit, 
  onClose,
  hideButtons,
  onDirtyChange
}, ref) => {
  const { t } = useTranslation('admin');
  const unitSchema = useMemo(() => buildUnitSchema(t), [t]);
  const utils = trpc.useUtils();
  const unitId = defaultValues?.unitId;
  
  const [formDefaultValues, setFormDefaultValues] = useState(defaultValues);
  const formRef = useRef<HTMLFormElement>(null);

  const createMutation = trpc.units.create.useMutation({
    onSuccess: () => utils.units.list.invalidate()
  });
  
  const updateMutation = trpc.units.update.useMutation({
    onSuccess: () => utils.units.list.invalidate()
  });

  // This exposes a submit method that can be called from the parent
  useImperativeHandle(ref, () => ({
    submit: () => {
      // Directly submit the form element
      if (formRef.current) {
        formRef.current.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
      }
    }
  }));

  const handleFormSubmit = async (data: UnitFormValues) => {
    try {
      let resultData = data;

      const mutationData = {
        name: data.name,
        description: data.description ?? undefined,
        womanId: data.womanId || null,
        empowermentPoints: data.empowermentPoints,
      };

      if (unitId) {
        // For update
        await updateMutation.mutateAsync({
          id: unitId,
          data: mutationData
        });
        toast.success(t('units.form.toasts.updated'));
        resultData = { ...data, womanId: mutationData.womanId, unitId };
      } else {
        // For create
        const created = await createMutation.mutateAsync(mutationData);
        toast.success(t('units.form.toasts.created'));
        resultData = { ...data, womanId: mutationData.womanId, unitId: created.unitId };
      }

      // The saved values become the new baseline, so the form reads as clean.
      setFormDefaultValues(resultData);
      onSubmit(resultData);
    } catch (error) {
      console.error("Error saving unit:", error);
      toast.error(t('units.form.toasts.saveFailed'));
    }
  };

  // Define custom labels for your form fields
  const labelMappings = {
    name: t('units.form.labels.name'),
    description: t('units.form.labels.description'),
    womanId: t('units.form.labels.woman'),
    empowermentPoints: t('units.form.labels.empowermentPoints'),
  };

  // Define fields to hide from the form
  const hiddenFields = ['unitId'];

  const customRenderers = {
    womanId: ({ field }: WomanFieldContext) => (
      <WomanSelect
        value={typeof field.value === 'string' && field.value ? field.value : null}
        onChange={(womanId) => field.onChange(womanId)}
      />
    ),
  };

  return (
    <DynamicForm
      schema={unitSchema}
      onSubmit={handleFormSubmit}
      defaultValues={formDefaultValues}
      isEditing={!!unitId}
      onClose={onClose}
      labelMappings={labelMappings}
      hiddenFields={hiddenFields}
      hideButtons={hideButtons}
      formRef={formRef}
      customRenderers={customRenderers as any}
      onDirtyChange={onDirtyChange}
    />
  );
}); 